import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getRefreshSettings, updateRefreshSettings, getSmtpSettings, updateSmtpSettings, testSmtpConnection } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import { Settings, Clock, Sun, Moon, Save, Loader2, Mail, Send } from 'lucide-react';

const INTERVAL_OPTIONS = [
  { value: '1440', label: '1 jour' },
  { value: '2880', label: '2 jours' },
  { value: '10080', label: '1 semaine' }
];

export const SettingsPage = () => {
  const { isAdmin, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // SMTP state
  const [smtp, setSmtp] = useState({ host: '', port: 587, username: '', from_email: '', has_password: false });
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpLoading, setSmtpLoading] = useState(true);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getRefreshSettings();
        setSettings(response.data);
      } catch (error) {
        toast.error('Erreur lors du chargement des paramètres');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();

    if (isAdmin) {
      const fetchSmtp = async () => {
        try {
          const response = await getSmtpSettings();
          setSmtp(response.data);
        } catch (_) {}
        finally { setSmtpLoading(false); }
      };
      fetchSmtp();
    } else {
      setSmtpLoading(false);
    }
  }, [isAdmin]);

  const handleSave = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      await updateRefreshSettings({
        auto_refresh: settings.auto_refresh,
        interval_minutes: parseInt(settings.interval_minutes)
      });
      toast.success('Paramètres sauvegardés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSmtpSave = async () => {
    setSmtpSaving(true);
    try {
      const payload = {
        host: smtp.host,
        port: smtp.port,
        username: smtp.username,
        from_email: smtp.from_email,
      };
      if (smtpPassword) {
        payload.password = smtpPassword;
      }
      const response = await updateSmtpSettings(payload);
      setSmtp(response.data);
      setSmtpPassword('');
      toast.success('Configuration SMTP sauvegardée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde SMTP');
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleSmtpTest = async () => {
    setSmtpTesting(true);
    try {
      // Save first, then test
      const payload = {
        host: smtp.host,
        port: smtp.port,
        username: smtp.username,
        from_email: smtp.from_email,
      };
      if (smtpPassword) {
        payload.password = smtpPassword;
      }
      const saveRes = await updateSmtpSettings(payload);
      setSmtp(saveRes.data);
      setSmtpPassword('');

      const response = await testSmtpConnection();
      toast.success(response.data?.message || 'Email de test envoyé');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Erreur lors du test SMTP', { duration: 8000 });
    } finally {
      setSmtpTesting(false);
    }
  };

  return (
    <Layout>
      <Header 
        title="Paramètres" 
        subtitle="Personnalisez votre expérience Cost Cloud"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column */}
        <div className="grid gap-6 content-start">
        {/* Theme Settings */}
        <Card data-testid="theme-settings-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              <div>
                <CardTitle className="text-lg font-[Outfit]">Apparence</CardTitle>
                <CardDescription>Personnalisez le thème de l'interface</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label>Thème sombre</Label>
                <p className="text-sm text-muted-foreground">
                  Activer le mode sombre pour réduire la fatigue oculaire
                </p>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                data-testid="theme-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Refresh Settings */}
        <Card data-testid="refresh-settings-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5" />
              <div>
                <CardTitle className="text-lg font-[Outfit]">Rafraîchissement des données</CardTitle>
                <CardDescription>Configurez la fréquence de mise à jour automatique</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {loading ? (
              <div className="h-24 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Rafraîchissement automatique</Label>
                    <p className="text-sm text-muted-foreground">
                      Mettre à jour les données automatiquement
                    </p>
                  </div>
                  <Switch
                    checked={settings?.auto_refresh}
                    onCheckedChange={(checked) => setSettings({...settings, auto_refresh: checked})}
                    disabled={!isAdmin}
                    data-testid="auto-refresh-switch"
                  />
                </div>

                {settings?.auto_refresh && (
                  <div className="space-y-2">
                    <Label>Intervalle de rafraîchissement</Label>
                    <Select
                      value={settings?.interval_minutes?.toString()}
                      onValueChange={(value) => setSettings({...settings, interval_minutes: parseInt(value)})}
                      disabled={!isAdmin}
                    >
                      <SelectTrigger data-testid="refresh-interval-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INTERVAL_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {settings?.last_refresh && (
                  <p className="text-sm text-muted-foreground">
                    Dernière mise à jour: {new Date(settings.last_refresh).toLocaleString('fr-FR')}
                  </p>
                )}

                {isAdmin && (
                  <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Sauvegarder
                  </Button>
                )}

                {!isAdmin && (
                  <p className="text-sm text-muted-foreground italic">
                    Seuls les administrateurs peuvent modifier ces paramètres.
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* User Info */}
        <Card data-testid="user-info-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5" />
              <div>
                <CardTitle className="text-lg font-[Outfit]">Mon compte</CardTitle>
                <CardDescription>Informations de votre compte</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <Label className="text-muted-foreground">Nom</Label>
                <p className="font-medium truncate">{user?.name}</p>
              </div>
              <div className="min-w-0">
                <Label className="text-muted-foreground">Email</Label>
                <p className="font-medium truncate">{user?.email}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Rôle</Label>
                <p className="font-medium capitalize">{user?.role}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Type de compte</Label>
                <p className="font-medium">SSO Microsoft</p>
              </div>
            </div>
          </CardContent>
        </Card>

        </div>

        {/* Right column - SMTP */}
        {isAdmin && (
        <div className="grid gap-6 content-start">
        {/* SMTP Settings */}
          <Card data-testid="smtp-settings-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5" />
                <div>
                  <CardTitle className="text-lg font-[Outfit]">Configuration SMTP</CardTitle>
                  <CardDescription>Configurez le serveur email pour les notifications d'alertes</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {smtpLoading ? (
                <div className="h-24 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp-host">Serveur SMTP</Label>
                      <Input
                        id="smtp-host"
                        value={smtp.host}
                        onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp-port">Port</Label>
                      <Input
                        id="smtp-port"
                        type="number"
                        value={smtp.port}
                        onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) || 587 })}
                        placeholder="587"
                      />
                      <p className="text-xs text-muted-foreground">
                        587 = STARTTLS (Gmail, Office 365) · 465 = SSL/TLS
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp-user">Utilisateur</Label>
                      <Input
                        id="smtp-user"
                        value={smtp.username}
                        onChange={(e) => setSmtp({ ...smtp, username: e.target.value })}
                        placeholder="user@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp-password">
                        Mot de passe
                        {smtp.has_password && !smtpPassword && (
                          <span className="text-xs text-muted-foreground ml-2">(déjà configuré)</span>
                        )}
                      </Label>
                      <Input
                        id="smtp-password"
                        type="password"
                        value={smtpPassword}
                        onChange={(e) => setSmtpPassword(e.target.value)}
                        placeholder={smtp.has_password ? '••••••••' : 'Mot de passe SMTP'}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-from">Adresse d'expédition</Label>
                    <Input
                      id="smtp-from"
                      value={smtp.from_email}
                      onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })}
                      placeholder="noreply@example.com"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button onClick={handleSmtpSave} disabled={smtpSaving}>
                      {smtpSaving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Sauvegarder
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleSmtpTest}
                      disabled={smtpTesting || !smtp.host}
                    >
                      {smtpTesting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Tester la connexion
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
        )}
      </div>
    </Layout>
  );
};
